import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Copy } from 'lucide-react'
import { useEffect, useState, type ReactNode, type RefObject } from 'react'
import { request } from '../../api/client'
import { keys } from '../../api/queries'
import type { PostDetail } from '../../api/types'
import { seekVideo } from '../../lib/cardControls'
import { formatTimecode } from '../../lib/format'
import { useViewState } from '../../lib/navigation'
import { Button } from '../Button'
import { copyOrShow } from '../ManualCopy'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'

/** Абзацы приходят с сервера; исходную речь и пунктуацию браузер не переписывает. */
export function ReelTranscript({ post, videoRef }: { post: PostDetail; videoRef: RefObject<HTMLVideoElement | null> }) {
  const [version, setVersion] = useViewState<'original' | 'translation'>(`post.${post.id}.transcript.version`, 'translation')
  const [timed, setTimed] = useViewState(`post.${post.id}.transcript.timed`, false)
  const english = post.language === 'en'
  const translated = english && version === 'translation'
  const original = post.transcript_formatted || post.transcript
  const text = translated ? post.translation_ru : original
  const noSpeech = post.transcript_status ? post.transcript_status === 'no_speech' : post.no_speech || post.language === 'no_speech'
  // Старый текст сохраняется при смене языка; сначала ждём новую расшифровку.
  const originalReady = post.transcript_status ? post.transcript_status === 'ready' : Boolean(original)
  const stage = translated && originalReady ? 'translation' : 'transcript'
  const status = stage === 'translation' ? post.translation_status : post.transcript_status
  const busy = status === 'pending' || status === 'processing'
  const failed = status === 'failed'

  if (post.kind !== 'reel') return <p className="text-sm text-muted">У карусели и картинки транскрипта нет.</p>
  if (noSpeech) return <p className="text-sm text-muted">В ролике нет речи — только музыка или шум.</p>

  return (
    <div>
      {english && (
        <div role="group" aria-label="Язык транскрипта" className="mb-3 flex gap-1 border-b border-line">
          <VersionButton selected={version === 'original'} onClick={() => setVersion('original')}>Оригинал · EN</VersionButton>
          <VersionButton selected={version === 'translation'} onClick={() => setVersion('translation')}>Перевод · RU</VersionButton>
        </div>
      )}
      {text ? (
        <div className="relative">
          <div className="mb-3 flex min-h-11 items-center justify-between gap-2">
            {!translated && post.segments.length > 0 ? (
              <div role="group" aria-label="Вид транскрипта" className="flex flex-wrap gap-1">
                <VersionButton selected={!timed} onClick={() => setTimed(false)}>Текст</VersionButton>
                <VersionButton selected={timed} onClick={() => setTimed(true)}>С таймкодами</VersionButton>
              </div>
            ) : <span className="text-sm text-muted">{translated ? 'Перевод на русский' : 'Транскрипт'}</span>}
            <CopyTranscript text={text} translation={translated} />
          </div>
          {!translated && timed && post.segments.length > 0 ? (
            <ol className="space-y-2">
              {post.segments.map((s, i) => (
                <li key={`${s.start}-${i}`} className="flex gap-2.5 text-[15px] leading-relaxed">
                  <button type="button" onClick={() => seekVideo(videoRef, s.start)} title="Перемотать сюда" className="min-h-11 shrink-0 self-start rounded px-1 font-mono text-xs text-muted tabular-nums hover:bg-surface hover:text-fg desk:min-h-8">
                    {formatTimecode(s.start)}
                  </button>
                  <span className="min-w-0 break-words pt-2.5 desk:pt-1">{s.text}</span>
                </li>
              ))}
            </ol>
          ) : <Paragraphs text={text} lang={translated ? 'ru' : post.language === 'en' ? 'en' : post.language === 'ru' ? 'ru' : undefined} />}
          {busy && <p role="status" className="mt-3 text-sm text-muted">Обновляем {stage === 'translation' ? 'перевод' : 'транскрипт'}…</p>}
          {failed && <Failure post={post} stage={stage} />}
        </div>
      ) : failed ? <Failure post={post} stage={stage} /> : (
        <div role="status" className="flex items-start gap-2 py-2 text-sm text-muted">
          {busy && <Spinner size={16} className="mt-0.5 shrink-0" />}
          <div>
            <p>{busy ? stage === 'translation' ? 'Готовим перевод…' : 'Расшифровываем ролик…' : translated ? 'Перевод пока недоступен.' : 'Транскрипта пока нет.'}</p>
            {translated && original && <button type="button" className="mt-1 min-h-11 text-fg underline underline-offset-4 desk:min-h-8" onClick={() => setVersion('original')}>Читать оригинал</button>}
          </div>
        </div>
      )}
      {!english && post.language === 'other' && <p className="mt-3 text-sm text-muted">Перевод доступен для роликов на английском языке.</p>}
    </div>
  )
}

function Paragraphs({ text, lang }: { text: string; lang?: string }) {
  return <div lang={lang} className="max-w-[72ch] space-y-4 text-[16px] leading-[1.6] break-words desk:text-[15px]">{text.split(/\n\s*\n/).map((paragraph, i) => <p key={i} className="whitespace-pre-line">{paragraph}</p>)}</div>
}

function VersionButton({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return <button type="button" aria-pressed={selected} onClick={onClick} className={`min-h-11 rounded-lg px-2.5 text-sm desk:min-h-8 ${selected ? 'bg-surface font-medium text-fg' : 'text-muted hover:text-fg'}`}>{children}</button>
}

function CopyTranscript({ text, translation }: { text: string; translation: boolean }) {
  const [done, setDone] = useState(false)
  useEffect(() => { setDone(false) }, [text])
  useEffect(() => {
    if (!done) return
    const timer = setTimeout(() => setDone(false), 1800)
    return () => clearTimeout(timer)
  }, [done])
  const label = done ? 'Скопировано' : translation ? 'Скопировать перевод' : 'Скопировать оригинал'
  return <Button size="icon" variant="ghost" aria-label={label} title={label} onClick={async () => setDone(await copyOrShow(text, { prose: true }))}>{done ? <Check size={18} aria-hidden /> : <Copy size={18} aria-hidden />}<span className="sr-only" aria-live="polite">{done ? 'Скопировано' : ''}</span></Button>
}

function Failure({ post, stage }: { post: PostDetail; stage: 'transcript' | 'translation' }) {
  const qc = useQueryClient()
  const retry = useMutation({
    mutationFn: () => request(`/api/posts/${post.id}/texts/retry`, { method: 'POST', body: { stage } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.post(post.id) }),
    onError: () => toast('Не удалось повторить обработку. Попробуйте ещё раз.', { error: true }),
  })
  return <div className="flex flex-wrap items-center gap-2 py-2 text-sm"><p className="text-muted">{stage === 'translation' ? 'Не удалось подготовить перевод.' : 'Не удалось расшифровать ролик.'}</p>{post.text_actions_allowed && <Button disabled={retry.isPending} onClick={() => retry.mutate()}>{retry.isPending && <Spinner size={14} />}Повторить</Button>}</div>
}
